/**
 * Scrubbing de identificadores sensibles antes de que salgan a Sentry.
 *
 * Card CW31sjZO (iteración #2). El QA leyó el envelope real que manda el SDK y
 * encontró el `session_id` del chat en breadcrumbs AUTOMÁTICOS: el
 * `console.log('[API-CLIENT] addAuthHeader called for: /api/v1/chat/sessions/{sid}/history')`
 * y la URL del xhr. Los campos que arma el código propio estaban limpios; lo
 * que filtraba era lo que el SDK junta solo.
 *
 * El `session_id` funciona como llave de lectura: `GET /sessions/{id}/history`
 * no chequea ownership (ver `chat_router.get_session_history`), así que quien
 * lo lea en Sentry puede leer la conversación.
 *
 * Qué se redacta, en cualquier string del breadcrumb / evento / transacción /
 * evento de replay:
 *
 * 1. El segmento que sigue a `/sessions/` o `/session/` (chat-service:
 *    `/chat/sessions/{id}/history|intro`, `DELETE /chat/sessions/{id}`,
 *    `/session/{id}/slots`). Se exceptúan `by-user` y `mine`, que son rutas,
 *    no ids.
 * 2. Valores de query params que son credenciales o llaves: `session_id`,
 *    `conversation_id`, `sid`, `token` (invitaciones), `code` / `state`
 *    (callback de Auth0), `access_token`, `id_token`.
 * 3. Cualquier UUID con guiones. Los ids de conversación salen de
 *    `crypto.randomUUID()` (`chatUIStore.generateConversationId`), así que esto
 *    cubre un id de sesión que aparezca fuera de una URL (un log, un mensaje
 *    de error). Los ids propios de Sentry (event, trace, span, replay) son hex
 *    SIN guiones y no matchean.
 *
 * Card mggUrIZI — TEXTO DEL USUARIO (regla ancha, no caso por caso):
 *
 * 4. El valor de los atributos de texto (`aria-label`, `title`, `alt`,
 *    `placeholder`, `value`, `aria-description`, `aria-valuetext`) dentro de
 *    un selector del DOM. El SDK nombra el elemento clickeado con
 *    `htmlTreeAsString`, que siempre agrega `[aria-label="…"]`, `[title="…"]`
 *    y `[alt="…"]`: en el sidebar el `aria-label` es «Cargar conversación:
 *    {título}», texto del usuario. Lo usan el breadcrumb `ui.click`, el
 *    nombre del span de INP (`ui.interaction.click`) y los breadcrumbs de
 *    Replay. Queda el tag, el id, las clases, `type` y `name`, y que el
 *    atributo existía: `button.x[aria-label="[redacted]"]`.
 * 5. Las mismas claves (salvo `value`) como CLAVE de un objeto
 *    (`{"aria-label": "…"}`), que es como los breadcrumbs de click de Replay
 *    guardan los atributos del nodo.
 * 6. Los valores de los params de texto libre en URLs (`q`, `query`,
 *    `search`, `text`, `message`, `email`, `phone`): la búsqueda híbrida, la
 *    semántica y el geocoding mandan lo que el usuario escribió en `?q=`, y
 *    los links de WhatsApp llevan el mensaje en `?text=`.
 * 7. El dump de un `TypeValidationError` del AI SDK (`Type validation
 *    failed: Value: {chunk entero}…`): el chunk lleva el texto de la
 *    conversación. Se conserva el prefijo para que el issue se reconozca.
 *
 * Lo que NO se redacta, a propósito: ids numéricos de DB en rutas
 * autenticadas (`/companies/43`, `/projects/12`…). No son llaves de acceso
 * —el endpoint exige JWT y permisos— y sirven para depurar.
 */

const REDACTED = '[redacted]';

/** `/sessions/<id>` y `/session/<id>`, salvo las rutas `by-user` y `mine`. */
const SESSION_PATH_RE = /(\/sessions?\/)(?!(?:by-user|mine)(?:[/?#"'\s]|$))[^/?#\s"'`]+/gi;

/**
 * Query params cuyo valor es una credencial, una llave de acceso o texto que
 * escribió el usuario (card mggUrIZI: `q`, `query`, `search`, `text`,
 * `message`, `email`, `phone`).
 */
const SENSITIVE_PARAMS =
  'session_id|conversation_id|sid|token|code|state|access_token|id_token|' +
  'q|query|search|text|message|email|phone';

/**
 * El valor de un param sensible, en la query (`?`/`&`) o en el fragmento
 * (`#`, por si algún flujo devuelve tokens ahí).
 */
const SENSITIVE_QUERY_RE = new RegExp(`([?&#](?:${SENSITIVE_PARAMS})=)[^&#\\s"'\`]+`, 'gi');

/** Sin el flag `g`: `test()` con `g` guarda `lastIndex` entre llamadas. */
const HAS_SENSITIVE_PARAM_RE = new RegExp(`[?&#](?:${SENSITIVE_PARAMS})=`, 'i');

const UUID_RE = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;

/**
 * Atributos del DOM cuyo valor es (o puede ser) texto que ve o escribe el
 * usuario. Card mggUrIZI. `type` y `name` NO están: los pone el código y
 * sirven para saber qué elemento era.
 */
export const USER_TEXT_ATTRIBUTES = [
  'aria-label',
  'title',
  'alt',
  'placeholder',
  'value',
  'aria-description',
  'aria-valuetext',
] as const;

/**
 * Las mismas, como CLAVE de un objeto. Sin `value`: en un evento de Sentry
 * `exception.values[].value` es el mensaje del error, no un atributo del DOM.
 */
const USER_TEXT_KEY_SET: ReadonlySet<string> = new Set(
  USER_TEXT_ATTRIBUTES.filter((attr) => attr !== 'value'),
);

/**
 * `[aria-label="…"]` dentro de un selector. El valor va hasta el `"]` que
 * cierra el atributo (seguido de otro atributo, del separador ` > `, de
 * puntuación o del fin del string) o hasta el fin del string, porque el SDK
 * recorta los strings largos (`maxValueLength`) y el `"]` puede no llegar.
 */
const USER_TEXT_SELECTOR_RE = new RegExp(
  `\\[(${USER_TEXT_ATTRIBUTES.join('|')})="[\\s\\S]*?(?:"\\](?=[\\[\\s"'),;]|$)|$)`,
  'gi',
);

/**
 * Lo que sigue a `Type validation failed` es el chunk rechazado y el reporte
 * de Zod con sus valores. Va hasta el fin del string.
 */
const WIRE_DUMP_RE = /(Type validation failed)[\s\S]*/g;

/** Profundidad máxima del recorrido: los eventos de Sentry no anidan más. */
const MAX_DEPTH = 12;

/**
 * ¿La URL lleva en la query o en el fragmento algún param sensible?
 *
 * Card CW31sjZO (iteración #3). Session Replay graba la URL de la página en
 * dos lugares que NINGÚN hook del SDK deja tocar: el evento `Meta` de rrweb
 * (`href`, en cada snapshot completo) y, hasta que el evento se arma, la lista
 * `urls` del replay. Por eso `initSentry` no arranca Replay mientras la URL
 * cumple esto (callback de Auth0 con `code`/`state`, invitación con `token`).
 */
export function hasSensitiveUrlParams(url: string): boolean {
  return HAS_SENSITIVE_PARAM_RE.test(url);
}

/** Redacta los patrones sensibles de un string. Idempotente. */
export function scrubString(value: string): string {
  return value
    .replace(WIRE_DUMP_RE, `$1: ${REDACTED}`)
    .replace(USER_TEXT_SELECTOR_RE, `[$1="${REDACTED}"]`)
    .replace(SESSION_PATH_RE, `$1${REDACTED}`)
    .replace(SENSITIVE_QUERY_RE, `$1${REDACTED}`)
    .replace(UUID_RE, REDACTED);
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/**
 * Redacta, EN SITIO, todos los strings de un valor JSON-like (objetos planos y
 * arrays). Devuelve el mismo valor para encadenarlo en los hooks del SDK.
 * No entra en instancias de clases (Error, Date, nodos del DOM): los eventos
 * que Sentry serializa son objetos planos.
 */
export function scrubDeep<T>(value: T, depth = 0, seen: WeakSet<object> = new WeakSet()): T {
  if (typeof value === 'string') return scrubString(value) as unknown as T;
  if (depth > MAX_DEPTH || value === null || typeof value !== 'object') return value;
  if (seen.has(value as object)) return value;
  seen.add(value as object);

  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i += 1) {
      value[i] = scrubDeep(value[i], depth + 1, seen);
    }
    return value;
  }
  if (isPlainObject(value)) {
    const record = value as Record<string, unknown>;
    for (const key of Object.keys(record)) {
      // Card mggUrIZI: `{"aria-label": "Cargar conversación: …"}` (atributos
      // del nodo en los breadcrumbs de click de Replay).
      if (USER_TEXT_KEY_SET.has(key.toLowerCase()) && typeof record[key] === 'string') {
        record[key] = REDACTED;
        continue;
      }
      record[key] = scrubDeep(record[key], depth + 1, seen);
    }
  }
  return value;
}

/** El tag que `logger` antepone a cada línea (`[ERROR]`, `[WARN]`…). */
const LOGGER_LEVEL_TAG_RE = /^\[(?:DEBUG|INFO|WARN|ERROR)\]$/;

/** Largo máximo del mensaje de un breadcrumb de consola. */
const CONSOLE_MESSAGE_MAX = 200;

/**
 * Breadcrumb de consola: sólo viaja el PRIMER argumento (salteando el tag de
 * nivel de `logger`), y sólo si es un string, recortado. Nunca los demás
 * argumentos ni los objetos: de ellos queda la cuenta (`[+2 args]`). Card
 * mggUrIZI.
 *
 * El SDK guarda en `data.arguments` todo lo que se pasó a `console.*` y arma
 * `message` uniéndolo. En la app hay ~300 llamadas a `console.*` y el patrón
 * es `console.error('[tag] qué pasó', payload)`: el primer argumento lo
 * escribe el código y el payload (un mensaje, una respuesta del backend, el
 * estado de un form) puede traer texto del usuario. No se puede auditar
 * llamada por llamada, así que la regla es por forma.
 *
 * Límite conocido: si el código interpola texto del usuario EN el primer
 * argumento (`console.error(\`falló ${file.name}\`)`), eso sí viaja.
 */
export function sanitizeConsoleBreadcrumb<T extends { category?: string; message?: string; data?: Record<string, unknown> }>(
  breadcrumb: T,
): T {
  if (breadcrumb.category !== 'console') return breadcrumb;
  const rawArgs = Array.isArray(breadcrumb.data?.arguments) ? (breadcrumb.data?.arguments as unknown[]) : [];
  const args = rawArgs.filter((arg) => !(typeof arg === 'string' && LOGGER_LEVEL_TAG_RE.test(arg)));
  const head = typeof args[0] === 'string' ? args[0] : undefined;
  const extra = args.length - (head === undefined ? 0 : 1);
  const base = head !== undefined ? head.slice(0, CONSOLE_MESSAGE_MAX) : '[console sin texto]';
  breadcrumb.message = extra > 0 ? `${base} [+${extra} args]` : base;
  if (breadcrumb.data) {
    const { arguments: _dropped, ...rest } = breadcrumb.data;
    void _dropped;
    breadcrumb.data = rest;
  }
  return breadcrumb;
}
